import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ChevronLeft, ChevronRight, Check, Loader2, MessageCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { publicApi, withRetry } from "@/lib/api";
import { BRANCHES, FALLBACK_SMS } from "@/lib/brand";

const FALLBACK_PHONE = FALLBACK_SMS.number;
const FALLBACK_PHONE_DISPLAY = FALLBACK_SMS.display;

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function pretty(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function timeLabel(t) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${pad(m)} ${ampm}`;
}

export default function Booking() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => {
    // Default to next available weekday from today
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsReason, setSlotsReason] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", branch: "",
    current_rating: "", filed_before: "", help_description: "",
    consent: true,
  });

  const days = useMemo(() => {
    // Show 7 days starting from weekStart
    const arr = [];
    const start = startOfWeek(weekStart);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const slotsRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (!selectedDate) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    withRetry(() => publicApi.get("/public/availability", { params: { date_str: ymd(selectedDate) } }))
      .then(({ data }) => {
        setSlots(data.slots || []);
        setSlotsReason(data.reason || null);
      })
      .catch(() => { setSlots([]); setSlotsReason("error"); })
      .finally(() => {
        setLoadingSlots(false);
        // Scroll the slot grid into view so mobile users immediately see the times
        setTimeout(() => {
          slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
      });
  }, [selectedDate]);

  // When a time is picked, slide the form into view on mobile
  useEffect(() => {
    if (!selectedTime) return;
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, [selectedTime]);

  const canSubmit = selectedDate && selectedTime && form.full_name && form.email && form.phone && form.branch && form.consent;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: ymd(selectedDate),
        time: selectedTime,
      };
      const { data } = await publicApi.post("/public/booking", payload);
      navigate("/booking-success", { state: { booking: data, date: ymd(selectedDate), time: selectedTime, name: form.full_name } });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="section bg-gradient-to-b from-white to-[#F4F6FB]">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto"
        >
          <p className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Book your free consultation</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1930] mt-3 leading-[1.05]">
            15 minutes. No cost. No pressure.
          </h2>
          <p className="mt-4 text-slate-600 text-base">Mon–Fri · 11:00 AM – 4:00 PM Eastern Time</p>
        </motion.div>

        <div className="mt-12 bg-white border border-slate-200 rounded-3xl p-5 md:p-8 shadow-[0_20px_60px_-40px_rgba(10,25,48,0.35)]" data-testid="booking-card">
          {/* Calendar strip */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#0A1930]">
              <CalendarDays className="w-4 h-4" />
              <span className="font-display text-sm tracking-wide">Pick a date</span>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { const n = new Date(weekStart); n.setDate(n.getDate() - 7); setWeekStart(n); }} className="h-9 w-9 rounded-full border border-slate-200 grid place-items-center hover:bg-slate-50" data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button type="button" onClick={() => { const n = new Date(weekStart); n.setDate(n.getDate() + 7); setWeekStart(n); }} className="h-9 w-9 rounded-full border border-slate-200 grid place-items-center hover:bg-slate-50" data-testid="week-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-2">
            {days.map((d) => {
              const dow = d.getDay();
              const disabled = dow === 0 || dow === 6 || d < new Date(new Date().setHours(0,0,0,0));
              const isSel = selectedDate && ymd(selectedDate) === ymd(d);
              return (
                <button
                  type="button"
                  key={ymd(d)}
                  disabled={disabled}
                  onClick={() => { setSelectedDate(d); setSelectedTime(null); }}
                  className={`flex flex-col items-center justify-center rounded-2xl border py-3 text-center transition-all
                    ${disabled ? "border-slate-100 text-slate-300 cursor-not-allowed" :
                      isSel ? "border-[#0066CC] bg-[#0066CC] text-white shadow-[0_8px_24px_-12px_rgba(0,102,204,.6)]" :
                      "border-slate-200 text-[#0A1930] hover:border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`day-${ymd(d)}`}
                >
                  <span className="text-[10px] tracking-[0.18em] uppercase opacity-80">{d.toLocaleDateString("en-US", { weekday: "short" })}</span>
                  <span className="font-display text-xl mt-1">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          {/* Time slots */}
          {selectedDate && (
            <div ref={slotsRef} className="mt-8 scroll-mt-24">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-slate-600">
                  <span className="text-[#0A1930] font-medium">Pick a time</span>
                  <span className="text-slate-500"> · {pretty(selectedDate)} (Eastern Time)</span>
                </div>
                {slots.length > 0 && (
                  <span className="text-[11px] tracking-wider uppercase text-slate-400">{slots.length} available</span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="slot-grid">
                {loadingSlots ? (
                  <div className="col-span-3 sm:col-span-5 flex items-center gap-2 text-slate-500 text-sm py-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</div>
                ) : slots.length === 0 ? (
                  <div className="col-span-3 sm:col-span-5 text-sm text-slate-500 py-2">
                    {slotsReason === "closed"
                      ? "We're closed on this day. Try a weekday."
                      : slotsReason === "error"
                      ? "Couldn't load times. Please try again in a moment."
                      : "All slots are booked on this date. Please pick another."}
                  </div>
                ) : (
                  slots.map((t) => (
                    <button key={t}
                      type="button"
                      onClick={() => setSelectedTime(t)}
                      className={`rounded-xl border py-2.5 text-sm transition-all active:scale-95
                        ${selectedTime === t ? "bg-[#0A1930] text-white border-[#0A1930]" : "border-slate-200 hover:border-slate-300 text-[#0A1930]"}`}
                      data-testid={`slot-${t}`}
                    >
                      {timeLabel(t)}
                    </button>
                  ))
                )}
              </div>

              {/* Robust fallback when slots fail to load (e.g., flaky mobile network) */}
              {!loadingSlots && slots.length === 0 && slotsReason === "error" && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="booking-error-fallback">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#0A1930] font-medium">Having trouble loading times?</p>
                      <p className="text-xs text-slate-600 mt-1">
                        You can text us directly and we'll book your free consult by hand. No app needed.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a
                          href={`sms:${FALLBACK_PHONE}?&body=${encodeURIComponent("Hi! I'd like to book a free VA disability consult. My name is ")}`}
                          className="inline-flex items-center gap-2 rounded-full bg-[#0066CC] text-white px-4 py-2 text-sm hover:bg-[#0055AA] active:scale-95"
                          data-testid="fallback-sms-cta"
                        >
                          <MessageCircle className="w-4 h-4" /> Text us — {FALLBACK_PHONE_DISPLAY}
                        </a>
                        <button
                          type="button"
                          onClick={() => setSelectedDate(new Date(selectedDate))}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1930] px-4 py-2 text-sm hover:bg-slate-50 active:scale-95"
                          data-testid="fallback-retry"
                        >
                          <RefreshCw className="w-4 h-4" /> Try again
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          {selectedTime && (
            <motion.form
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 scroll-mt-24"
              data-testid="booking-form"
              ref={formRef}
            >
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name *">
                  <Input data-testid="form-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="h-12" />
                </Field>
                <Field label="Email *">
                  <Input data-testid="form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-12" />
                </Field>
              </div>

              <Field label="Phone number *">
                <Input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="h-12" />
              </Field>
              <Field label="Branch of service *">
                <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                  <SelectTrigger className="h-12" data-testid="form-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Current VA rating (if any)">
                <Input data-testid="form-rating" placeholder="e.g., 30% or None" value={form.current_rating} onChange={(e) => setForm({ ...form, current_rating: e.target.value })} className="h-12" />
              </Field>
              <Field label="Have you filed before?">
                <Select value={form.filed_before} onValueChange={(v) => setForm({ ...form, filed_before: v })}>
                  <SelectTrigger className="h-12" data-testid="form-filed"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Unsure">Unsure</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <div className="md:col-span-2">
                <Field label="What help do you need?">
                  <Textarea data-testid="form-help" rows={4} value={form.help_description} onChange={(e) => setForm({ ...form, help_description: e.target.value })} placeholder="Briefly describe your situation…" />
                </Field>
              </div>

              <div className="md:col-span-2 flex items-start gap-3">
                <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="form-consent" />
                <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed">
                  I consent to receive email and text reminders related to my consultation. Reply STOP to opt out at any time.
                </label>
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
                <div className="text-sm text-slate-500">
                  Selected: <span className="text-[#0A1930] font-medium">{pretty(selectedDate)} · {timeLabel(selectedTime)} ET</span>
                </div>
                <Button type="submit" disabled={submitting || !canSubmit}
                  className="h-12 rounded-full px-7 bg-[#0066CC] hover:bg-[#0055AA]" data-testid="form-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Booking…</> : <><Check className="w-4 h-4 mr-2" />Confirm booking</>}
                </Button>
              </div>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs tracking-[0.12em] uppercase text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
